/* istanbul ignore file */
import React from 'react';

import { useHistory } from 'react-router-dom';

import { get as getConstants } from '@lumapps/constants';
import { useDispatch, useStore } from '@lumapps/redux/react';

import { RouterApi, OnAfterRouteHook, OnBeforeRouteHook } from '../api';
import { AppId } from '../constants';
import { isRedirectionBlocked, currentRouteSelector } from '../ducks/selectors';
import { actions as routerActions } from '../ducks/slice';
import { webviewRouterApi, Context, routerApi, angularApi } from '../routers';
import { Route } from '../types';
import { createUrl, isAdminUrl } from '../utils';

const { isLegacyContext, applicationId } = getConstants();

const onBeforeRouteHooks: OnBeforeRouteHook[] = [];
const onAfterRouteHooks: OnAfterRouteHook[] = [];

export const runOnBeforeHooks = (
    to: Route,
    from: Route,
    hooks: OnBeforeRouteHook[],
    appId: AppId = applicationId,
): { shouldRedirect: boolean; shouldRedirectTo?: Route } => {
    let shouldRedirect = true;
    let shouldRedirectTo;
    /*
     * We loop through the onBeforeRoute hooks until one of them
     * cancels the redirection (by calling next(false)) or redirects
     * to another path (by calling next('/path')).
     */
    for (let i = 0; i < hooks.length && (shouldRedirect || shouldRedirectTo); i += 1) {
        const hook = hooks[i];

        // eslint-disable-next-line no-loop-func
        hook(to, from, (res) => {
            if (res === false) {
                shouldRedirect = false;
            }

            if (typeof res === 'object') {
                shouldRedirectTo = res;
            }

            if (typeof res === 'string') {
                shouldRedirectTo = {
                    path: res,
                    appId,
                };
            }
        });
    }

    if (shouldRedirectTo) {
        shouldRedirect = true;
    }

    return { shouldRedirect, shouldRedirectTo };
};

/* istanbul ignore next */
export const useRouter = (options?: { appId: AppId }): RouterApi => {
    const appId = options?.appId || applicationId;
    const history = useHistory();
    const currentContext = React.useContext(Context);
    const { getRedirect, getUrl: contextGetUrl } = currentContext;
    const store = useStore<BaseStore>();

    const dispatch = useDispatch();

    const redirect = React.useMemo<RouterApi['redirect']>(() => {
        // Angular router
        if (isLegacyContext) {
            return angularApi.redirect;
        }
        // Webview app router.
        if (appId === AppId.webview) {
            return (route, options = {}) => webviewRouterApi.redirect(route, history, options);
        }

        // Web components router
        if (getRedirect) {
            const redirect = getRedirect({ store });
            return redirect;
        }

        // React router
        return (route, { routerData, openInNewTab, replace, keepFocus, ignoreBlockedRedirection } = {}) => {
            const currentState = store.getState();
            const isRoutingBlocked = isRedirectionBlocked(currentState);
            const currentRoute = currentRouteSelector(currentState);

            const { shouldRedirect, shouldRedirectTo } = runOnBeforeHooks(
                route,
                currentRoute as Route,
                onBeforeRouteHooks,
            );

            if (shouldRedirect) {
                // On redirect, store current location into router state
                const routeWithPreviousPath = shouldRedirectTo || {
                    ...route,
                    state: {
                        previousPath: history?.location?.pathname,
                        ...route.state,
                    },
                };

                // Store router data.
                if (routerData) {
                    dispatch(routerActions.setData(routerData));
                }

                if (!openInNewTab && isRoutingBlocked && !ignoreBlockedRedirection) {
                    // If the routing is blocked, we must set the redirection information in order
                    // to continue the redirection if the user unblocks it.
                    // Once the route is set, the BlockedRedirectionDialog will open.
                    dispatch(routerActions.setBlockedRedirection({ route, history }));
                } else {
                    routerApi.redirect(routeWithPreviousPath, history, { openInNewTab, replace, keepFocus });

                    /**
                     * If the redirection block was ignore, propagate that on redux so that the next navigation works
                     * as expected
                     */
                    if (ignoreBlockedRedirection) {
                        dispatch(routerActions.unblockRedirection());
                    }
                }
            }
        };
    }, [appId, dispatch, getRedirect, history, store]);

    const currentUrl = window.location.pathname;

    const getUrl = React.useMemo<RouterApi['getUrl']>(() => {
        if (contextGetUrl) {
            const generatedGetUrl = contextGetUrl({ store });
            return generatedGetUrl;
        }
        return createUrl;
    }, [contextGetUrl, store]);

    return {
        registerOnBeforeRedirect: (callback: OnBeforeRouteHook) => {
            onBeforeRouteHooks.push(callback);
        },
        registerOnAfterRedirect: (callback: OnAfterRouteHook) => {
            onAfterRouteHooks.push(callback);
        },
        unregisterOnAfterRedirect: (callback: OnAfterRouteHook) => {
            const index = onAfterRouteHooks.indexOf(callback);
            if (index > -1) {
                onAfterRouteHooks.splice(index, 1);
            }
        },
        unregisterOnBeforeRedirect: (callback: OnBeforeRouteHook) => {
            const index = onBeforeRouteHooks.indexOf(callback);
            if (index > -1) {
                onBeforeRouteHooks.splice(index, 1);
            }
        },
        onAfterRoute: (to, from) => {
            onAfterRouteHooks.forEach((hook) => {
                hook(to, from);
            });
        },
        getUrl,
        redirect,
        context: {
            ...currentContext,
            isBackOffice: isAdminUrl(currentUrl),
        },
    };
};
